import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';

import type { WebinarServiceRB } from 'rainbow-web-sdk/services/webinars/webinars.service';
import { WEBINAR_SVC } from 'rainbow-web-sdk/services/webinars/webinars.service';
import type { BubbleConferenceServiceRB } from 'rainbow-web-sdk/services/conference/bubbleConference.service';
import { BUBBLE_CONFERENCE_SVC } from 'rainbow-web-sdk/services/conference/bubbleConference.service';
import type { WebrtcP2PService } from 'rainbow-web-sdk/services/webrtc/webrtcP2P.service';
import { WEBRTC_P2P_SVC } from 'rainbow-web-sdk/services/webrtc/webrtcP2P.service';
import type { WebrtcConfigAndUtils } from 'rainbow-web-sdk/services/webrtc/webrtcConfigAndUtils';
import { WEBRTC_CONFIG_UTILS_SVC } from 'rainbow-web-sdk/services/webrtc/webrtcConfigAndUtils';
import { Subject, Subscription } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';

export const PRESENCE_HELPER_SVC = 'PresenceHelperService';

/**
 * @public
 * @description PresenceHelperService Events.
 */
export enum PresenceHelperServiceEvents {
    /**
     * @event RECONNECT_PRESENCE_SENT
     * @description This RB event is send after reconnection, when the initial presence is being send to xmpp (we consider that the client is now fully connected)
     */
    RECONNECT_PRESENCE_SENT = 'RECONNECT_PRESENCE_SENT',

    /**
     * @event ALL_CALLS_ENDED
     * @description This RB event is send when all calls are ended
     */
    ALL_CALLS_ENDED = 'ALL_CALLS_ENDED'
}

export class PresenceHelperService extends Service {

    private mainService = MainService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private webinarService?: WebinarServiceRB = undefined;
    private webrtcP2PService?: WebrtcP2PService = undefined;
    private webConferenceService?: BubbleConferenceServiceRB = undefined;
    private webrtcConfigAndUtils?: WebrtcConfigAndUtils = undefined;
    private eventListeners: any = [];
    private rxSubject: Subject<RBEvent> = new Subject<RBEvent>();

    public static getInstance() {
        return Service.create(PRESENCE_HELPER_SVC, PresenceHelperService);
    }

    public static build() {
        return new PresenceHelperService();
    }

    /**
     * @public
     * @function subscribe
     * @instance
     * @description
     *    Subscribe to updates on the current calls sessions (all events are of RBEvent type {name: string, data:any});
     *    All public events that are fired by this service are described in the documentation.
     *    NB There might be some events that are not documented here, they should be ignored
     * @param {any} handler The call-back function that will be subscribed to the RxJS subject
     * @returns {Subscription} Returns RxJS Subscription
     */
    public subscribe(handler: any): Subscription {
        return this.rxSubject.subscribe(handler);
    }

    private constructor() { super(); }

    public async start() {
        this.logger.info("[PresenceHelperService] === STARTING ===");

        this.eventListeners.push(this.eventService.subscribe('CHECK_AND_RESET_BUSY_STATE', () => {
            this.resetBusyStateIfNeeded();
        }));

        this.webrtcConfigAndUtils = this.mainService.inject<WebrtcConfigAndUtils>(WEBRTC_CONFIG_UTILS_SVC);
        this.webinarService = this.mainService.inject<WebinarServiceRB>(WEBINAR_SVC);
        this.webConferenceService = this.mainService.inject<BubbleConferenceServiceRB>(BUBBLE_CONFERENCE_SVC);
        this.webrtcP2PService = this.mainService.inject<WebrtcP2PService>(WEBRTC_P2P_SVC);

        this.logger.info("[PresenceHelperService] === STARTED ===");
    }

    public stop() {
        this.eventListeners.forEach((listener) => {
            listener();
        });
        this.eventListeners = [];
    }

    public resendPresenceAfterReconnect(): void {
        setTimeout(async () => {
            this.logger.debug(`[PresenceHelperService] resendPresenceAfterReconnect`);
            const inCall = this.isUserCurrentlyInCall();
            if (inCall && !this.contactService.manualState) {
                this.contactService.setBusyState("dnd", "audio");
            }
            else {
                await this.contactService.sendPresenceFromConfiguration(true);
            }
            //add event that the presence has been send
            this.rxSubject.next(RBEvent.create(PresenceHelperServiceEvents.RECONNECT_PRESENCE_SENT, null));
        }, 100);
    }

    public resetBusyStateIfNeeded(): void {
        setTimeout(() => {
            this.logger.debug(`[PresenceHelperService] resetBusyStateIfNeeded`);
            const inCall = this.isUserCurrentlyInCall();
            if (!inCall) {
                this.contactService.resetBusyState();
                // Clean the srcObject from the global audio tag
                const globalAudioTagElem: any = document.getElementById("globalAudioTag");
                if (globalAudioTagElem?.srcObject && !globalAudioTagElem.srcObject.active) {
                    this.webrtcConfigAndUtils?.RTC.clearMediaStream([globalAudioTagElem]);
                }

                this.rxSubject.next(RBEvent.create(PresenceHelperServiceEvents.ALL_CALLS_ENDED, null));
            }
        }, 100);
    }

    /**
     * Method isUserCurrentlyInCall
     * @public
     * @memberof PresenceHelperService
     * @returns {boolean} True if user is currently in a webrtc call or conference or webinar
     * The function will return true if the user is currently engaged in any kind of webrtc call / conference
     * This will not return true if the user has PBX calls in office mode, but only when
     * the call or the conference are done on this current device;
     */
    public isUserCurrentlyInCall(): boolean {
        //this takes into account both webrtc and Computer Mode (MP) calls
        const isInVideoCall = this.webrtcP2PService?.isUserContactInCall();
        const isInConferenceV2Call = this.webConferenceService?.hasActiveWebConferenceSession();
        const isInWebinar = (this.webinarService?.conference?.hasActiveWebinarSession());
        if (isInConferenceV2Call || isInVideoCall || isInWebinar) {
            this.logger.debug(`[PresenceHelperService] user is currently in a call -- ${isInVideoCall} / ${isInConferenceV2Call} / ${isInWebinar}`);
            return true;
        }
        return false;
    }
}
